/**
 * Footer Component
 *
 * 3-column footer: Logo+CTA | Contact | Office Hours.
 * Below: compliance icons, then an attribution bar with Neighborly (managed by),
 * the Cobalt Built Community mark, Multihub, and Repli.
 */

import { useEffect, type CSSProperties } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  useProperty,
  usePropertyAddressUrl,
  usePropertyField,
  useTokens,
  EditableToken,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import {
  FairHousingIcon,
  PetsAllowedIcon,
  WheelchairIcon,
  multihubLogoMonoWhiteUrl,
  multihubLogoMonoBlackUrl,
  repliWhiteLogoUrl,
} from '@vibesites/assets';
import { formatPhone } from '../utils/format-phone';
import { pickContrastLogo, isDarkBackground } from '../utils/contrast-logo';
import neighborlyLogoUrl from '../assets/neighborly-black.png';
import cobaltBuiltLogoUrl from '../assets/cobalt-built-black.png';

// ============================================================================
// Types & Helpers
// ============================================================================

function ensureAbsoluteUrl(url: string | undefined | null): string {
  if (!url || url === '#') return url || '#';
  const trimmed = url.trim();
  if (/^https?:\/\//i.test(trimmed) || /^mailto:/i.test(trimmed) || /^tel:/i.test(trimmed)) {
    return trimmed;
  }
  if (trimmed.startsWith('#') || trimmed.startsWith('/')) {
    return trimmed;
  }
  return `https://${trimmed}`;
}

/**
 * Resolve a compliance-page URL (Accessibility, Privacy Policy) to an
 * absolute URL. Handles four cases so the result is always correct regardless
 * of what Vibesites has stored for the token:
 *
 *  1. No value / '#' / legacy richtext HTML  → derive from websiteUrl + subPath
 *  2. Root-relative path ("/accessibility")  → prepend absolute websiteUrl
 *  3. Bare domain ("www.site.com/path")      → add https://
 *  4. Already-absolute URL                   → use as-is
 */
function resolveComplianceUrl(
  tokenUrl: string | null | undefined,
  subPath: string,
  websiteUrl: string | null,
): string {
  const absoluteBase = websiteUrl
    ? ensureAbsoluteUrl(websiteUrl).replace(/\/+$/, '')
    : '';
  const fallback = `${absoluteBase}/${subPath}`;
  if (!tokenUrl || tokenUrl === '#' || tokenUrl.trim().startsWith('<')) return fallback;
  const t = tokenUrl.trim();
  if (t.startsWith('/')) return `${absoluteBase}${t}`;
  return ensureAbsoluteUrl(t);
}

function buildCtaUrl(baseUrl: string | null, queryParams: string | null): string {
  const url = ensureAbsoluteUrl(baseUrl);
  if (!queryParams || queryParams.trim() === '' || queryParams.trim() === '?utm_source=') {
    return url;
  }
  const params = queryParams.trim().startsWith('?') ? queryParams.trim() : `?${queryParams.trim()}`;
  if (url.includes('?')) {
    return `${url}&${params.slice(1)}`;
  }
  return `${url}${params}`;
}

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

interface GroupedHours {
  label: string;
  hours: string;
}

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  }
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<string, string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  const sorted = [...raw].sort((a, b) => {
    const ai = DAY_ORDER.indexOf(normalizeDay(a.day || ''));
    const bi = DAY_ORDER.indexOf(normalizeDay(b.day || ''));
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  const groups: GroupedHours[] = [];
  for (const entry of sorted) {
    if (!hasValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    const dayLabel = DAY_LABELS[key] || entry.day;
    const isClosed = !hasValue(entry.open_time) && !hasValue(entry.close_time);
    const hoursStr = isClosed
      ? 'Closed'
      : `${formatTime(entry.open_time || '')} - ${formatTime(entry.close_time || '')}`;

    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      const dash = prev.label.indexOf(' - ');
      const startLabel = dash !== -1 ? prev.label.slice(0, dash) : prev.label;
      prev.label = `${startLabel} - ${dayLabel}`;
    } else {
      groups.push({ label: dayLabel, hours: hoursStr });
    }
  }
  return groups;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Footer Logo',
  fields: {
    // No defaultField — Footer.tsx picks mono white vs mono black at render
    // time based on the primary color luminance (footer sits over bg-primary).
    image: {
      label: 'Logo Image',
      type: 'image',
      suggestedFields: [
        'property_mono_white_logo',
        'property_mono_black_logo',
        'property_primary_logo',
        'property_secondary_logo',
      ],
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const managedByLogoDef: SlotGroupDefinition = {
  label: 'Managed By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Neighborly Communities' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.theneighborlyway.com/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const cobaltBuiltLogoDef: SlotGroupDefinition = {
  label: 'Cobalt Built Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'A Cobalt Built Community' },
    url: { label: 'URL', type: 'url', defaultField: 'property_company_url', defaultValue: 'https://cobaltbuilt.com/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const addressSlotDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    address: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street',
    },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at render
    // time. Editor overrides still win.
    url: {
      label: 'URL',
      type: 'url',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

const ctaLinkSlotDef: SlotGroupDefinition = {
  label: 'Visit Website CTA',
  fields: {
    label: {
      label: 'CTA Text',
      type: 'text',
      defaultValue: 'Visit Website',
    },
    url: {
      label: 'CTA URL',
      type: 'url',
      defaultField: 'property_website_url',
      suggestedFields: ['property_website_url', 'property_apply_now_url'],
    },
    query_params: {
      label: 'URL Query Parameters',
      type: 'text',
      description: 'Query string appended to the URL, e.g. ?utm_medium=ppc&utm_source=google&utm_campaign=landing-page',
      defaultValue: '?utm_source=',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

// ============================================================================
// Component
// ============================================================================

export function Footer() {
  // Content tokens (defaults in contentSchema)
  const phoneToken = useContentToken('footer.phone');
  const trackingPhone = usePropertyField('property_call_tracking_phone') as string | null;
  // Fallback chain: editor override / custom_ppc_tracking_phone_number → property_call_tracking_phone
  const phone = (phoneToken && phoneToken.trim()) ? phoneToken : (trackingPhone || '');
  const managedByText = useContentToken('footer.managed_by_text');
  const copyrightRaw = useContentToken('footer.copyright');
  const copyrightText = copyrightRaw?.replace(/© \d{4}/, `© ${new Date().getFullYear()}`) ?? copyrightRaw;
  // Compliance link URLs — resolved to absolute at render time from property_website_url
  const websiteUrl = usePropertyField('property_website_url') as string | null;
  const accessibilityTokenUrl = useContentToken('footer.accessibility_link') as string | null;
  const privacyTokenUrl = useContentToken('footer.privacy_link') as string | null;
  const accessibilityHref = resolveComplianceUrl(accessibilityTokenUrl, 'accessibility', websiteUrl);
  const privacyHref = resolveComplianceUrl(privacyTokenUrl, 'privacy-policy', websiteUrl);

  // Address link URL — defaults to usePropertyAddressUrl() (which falls back
  // through google_place_url → google_business_url → property_full_address →
  // lat/lon). Editor overrides on the slot still win at render time.
  const propertyAddressUrl = usePropertyAddressUrl();

  // Slot groups
  const logo = useSlotGroup('footer.logo', logoSlotDef);
  const managedByLogo = useSlotGroup('footer.managed_by_logo', managedByLogoDef);
  const cobaltBuiltLogo = useSlotGroup('footer.cobalt_built_logo', cobaltBuiltLogoDef);
  const ctaLink = useSlotGroup('footer.cta_link', ctaLinkSlotDef);
  // Address slot — `address` text defaults to property_full_address; `url`
  // defaults to the address-URL hook above.
  const address = useSlotGroup('footer.address', addressSlotDef);

  // The footer sits over `bg-primary`, so we default the community logo and
  // the attribution marks to whichever mono variant matches the design
  // system's contrast text token. `textOnPrimary` is already computed by
  // `@vibesites/tokens` (WCAG luminance check). Neighborly and the Cobalt
  // Built mark ship as black PNGs, so on a dark primary they are inverted
  // to white. Editor uploads on the community logo still win.
  const property = useProperty();
  const tokens = useTokens();
  const textOnPrimary = tokens.color.textOnPrimary;
  const smartDefaultLogo = pickContrastLogo(textOnPrimary, {
    monoWhite: property.property_mono_white_logo,
    monoBlack: property.property_mono_black_logo,
    fallback: property.property_primary_logo,
  });
  const resolvedLogo = logo.image || smartDefaultLogo;

  // Multihub ships both mono-white and mono-black SVGs — pick the matching
  // one. Repli only ships a white SVG, so on light backgrounds we invert it
  // via CSS filter (textOnPrimary is always #ffffff or #1a1a1a, so a binary
  // invert is sufficient to match the contrast token).
  const onDarkBg = isDarkBackground(textOnPrimary);
  const multihubLogoUrl = onDarkBg ? multihubLogoMonoWhiteUrl : multihubLogoMonoBlackUrl;
  const repliFilterStyle = onDarkBg ? undefined : { filter: 'invert(1)' };
  // Neighborly and Cobalt Built are black source art.
  const brandMarkFilter = onDarkBg ? { filter: 'brightness(0) invert(1)' } : undefined;
  // Same box for every attribution mark so wordmarks of different aspect
  // ratios still read as one size in the row.
  const attributionLogoStyle: CSSProperties = {
    height: '36px',
    width: 'auto',
    maxWidth: '240px',
    objectFit: 'contain',
  };

  // Office hours
  const officeHours = useCollectionBinding<OfficeHour>('footer.office_hours', {
    collection: 'office_hours',
  });
  const groupedHours = officeHours && officeHours.length > 0
    ? groupOfficeHours(officeHours)
    : [];

  // ─── RentCafe Connect launcher z-index pin ─────────────────────────────────
  // The launcher is injected at runtime and then writes its own inline
  // `style="z-index: …"`, which beats a normal stylesheet rule. `setProperty`
  // with the `important` priority is what makes that inline value lose.
  //
  // Only the real launcher is touched:
  //   .rcc-app-cta-wrap, .rcc-app-controls, #conversations-webchat-toggle
  // An outermost `position: fixed` ancestor is pinned only when that ancestor
  // is the chat wrap, so a surrounding page shell is left alone. The value
  // sits just above the open header.
  //
  // Scoped to the Footer because Footer is rendered on every page via
  // DefaultLayout, so we get a single global mount point without having to
  // introduce a new always-on component.
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const LAUNCHER_SELECTOR =
      '.rcc-app-cta-wrap, .rcc-app-controls, #conversations-webchat-toggle';
    const CHAT_WRAP_SELECTOR = '.rcc-app-cta-wrap';
    const Z = '120';

    function outermostFixedAncestor(el: Element): HTMLElement | null {
      let cur: Element | null = el;
      let last: HTMLElement | null = null;
      while (cur && cur !== document.body && cur instanceof HTMLElement) {
        if (getComputedStyle(cur).position === 'fixed') last = cur;
        cur = cur.parentElement;
      }
      return last;
    }

    function pin(el: HTMLElement) {
      el.style.setProperty('z-index', Z, 'important');
    }

    function processCandidate(el: Element) {
      if (!(el instanceof HTMLElement) || !el.matches(LAUNCHER_SELECTOR)) return;
      pin(el);
      const fixedAncestor = outermostFixedAncestor(el);
      if (
        fixedAncestor &&
        fixedAncestor !== el &&
        fixedAncestor.matches(CHAT_WRAP_SELECTOR)
      ) {
        pin(fixedAncestor);
      }
    }

    function scanSubtree(root: ParentNode) {
      const candidates = root.querySelectorAll(LAUNCHER_SELECTOR);
      for (const c of Array.from(candidates)) processCandidate(c);
      // querySelectorAll skips the root node itself.
      if (root instanceof Element) processCandidate(root);
    }

    // Initial pass — catches the widget if it's already mounted by the time
    // the Footer hydrates (e.g. injected from <head> before React boots).
    scanSubtree(document.body);

    // Coalesce bursts of mutations into a single rAF-scheduled scan so we
    // don't thrash on chat widgets that animate (every transform tick fires
    // an attribute mutation).
    let scheduled = false;
    const observer = new MutationObserver((records) => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        for (const r of records) {
          for (const node of Array.from(r.addedNodes)) {
            if (node instanceof Element) scanSubtree(node);
          }
          if (r.type === 'attributes' && r.target instanceof Element) {
            processCandidate(r.target);
          }
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      // Class/id so a node that becomes the launcher is caught; style so a
      // later inline z-index from the vendor is overwritten again.
      attributeFilter: ['id', 'class', 'style'],
    });

    return () => observer.disconnect();
  }, []);

  return (
    <footer className="bg-primary text-primary-foreground">
      {/* Main footer content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-12 md:gap-14">

          {/* Column 1: Logo + Visit Website CTA */}
          <div className="flex flex-col items-center sm:items-start gap-5">
            <EditableSlotGroup groupPath="footer.logo" definition={logoSlotDef}>
              <a href="/" className="block">
                {resolvedLogo ? (
                  <VibesitesImage
                    url={resolvedLogo}
                    alt={logo.alt || 'Community Logo'}
                    sizes="200px"
                    className="h-20 w-auto max-w-[200px] object-contain"
                  />
                ) : (
                  <div className="font-heading text-2xl text-primary-foreground">Our Community</div>
                )}
              </a>
            </EditableSlotGroup>

            <EditableSlotGroup groupPath="footer.cta_link" definition={ctaLinkSlotDef}>
              <a
                href={buildCtaUrl(ctaLink.url, ctaLink.query_params)}
                target={ctaLink._target || '_blank'}
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-md bg-secondary px-6 py-3 font-body text-sm font-semibold text-secondary-foreground no-underline transition-all duration-200 hover:opacity-90 hover:shadow-lg"
              >
                {ctaLink.label || 'Visit Website'}
              </a>
            </EditableSlotGroup>
          </div>

          {/* Column 2: Contact */}
          <div className="flex flex-col items-center sm:items-start gap-5 text-center sm:text-left">
            <div>
              <p className="font-body text-[11px] uppercase tracking-[0.15em] text-primary-foreground/70 mb-1">Call Us</p>
              <a
                href={phone ? `tel:${phone.replace(/[^+\d]/g, '')}` : '#'}
                className="font-body text-sm text-primary-foreground/80 hover:text-primary-foreground transition-colors duration-200"
              >
                <EditableToken path="footer.phone" as="span">
                  {formatPhone(phone)}
                </EditableToken>
              </a>
            </div>

            <div>
              <p className="font-body text-[11px] uppercase tracking-[0.15em] text-primary-foreground/70 mb-1">Visit Us</p>
              <EditableSlotGroup groupPath="footer.address" definition={addressSlotDef}>
                <a
                  href={address.url || propertyAddressUrl || '#'}
                  target={address._target || '_blank'}
                  rel="noopener noreferrer"
                  className="font-body text-sm text-primary-foreground/80 hover:text-primary-foreground transition-colors duration-200 block"
                >
                  {address.address}
                </a>
              </EditableSlotGroup>
            </div>
          </div>

          {/* Column 3: Office Hours */}
          <div className="flex flex-col items-center sm:items-start text-center sm:text-left">
            <p className="font-body text-[11px] uppercase tracking-[0.15em] text-primary-foreground/70 mb-3">Office Hours</p>
            {groupedHours.length > 0 ? (
              <dl className="space-y-2 w-full max-w-[320px]">
                {groupedHours.map((group, index) => (
                  <div key={index} className="flex justify-between font-body text-sm text-primary-foreground/80 hover:text-primary-foreground/90 transition-colors duration-200">
                    <dt>{group.label}</dt>
                    <dd className="text-right">{group.hours}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="font-body text-sm text-primary-foreground/70">Office hours will be displayed here.</p>
            )}
          </div>


        </div>
      </div>

      {/* Compliance Icons + Legal Links + Attribution — unified themed section */}
      <div>
        {/* Compliance icons & legal links */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-4">
          <div className="flex flex-col items-center gap-4">
            <div className="flex items-center gap-8 text-primary-foreground/70">
              <FairHousingIcon size={24} color="currentColor" />
              <PetsAllowedIcon size={24} color="currentColor" />
              <WheelchairIcon size={24} color="currentColor" />
            </div>
            <nav className="flex items-center gap-2 font-body text-xs text-primary-foreground/70 [&_a]:text-primary-foreground/70 [&_a:hover]:text-primary-foreground/90 [&_a]:transition-colors [&_a]:duration-200 [&_a]:no-underline" aria-label="Legal links">
              <EditableToken path="footer.accessibility_link" as="a" {...{ href: accessibilityHref }}>Accessibility</EditableToken>
              <span aria-hidden="true">/</span>
              <EditableToken path="footer.privacy_link" as="a" {...{ href: privacyHref }}>Privacy Policy</EditableToken>
            </nav>
            <EditableToken path="footer.copyright" as="span" className="font-body text-xs text-primary-foreground/70">
              {copyrightText}
            </EditableToken>
          </div>
        </div>

        {/* Attribution logos */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10">
          <div className="flex flex-wrap items-end justify-center gap-x-10 gap-y-8 lg:justify-between">
            {/* Professionally Managed By Neighborly */}
            <div className="flex flex-col items-center">
              <span className="font-body text-[11px] uppercase tracking-[0.15em] text-primary-foreground/60 mb-3">
                <EditableToken path="footer.managed_by_text" as="span">
                  {managedByText}
                </EditableToken>
              </span>
              <div className="flex h-9 w-[240px] items-center justify-center">
                <EditableSlotGroup groupPath="footer.managed_by_logo" definition={managedByLogoDef}>
                  <a href={managedByLogo.url || 'https://www.theneighborlyway.com/'} target={managedByLogo._target || '_blank'} rel="noopener noreferrer" className="block hover:opacity-80 transition-opacity duration-200">
                    <img
                      src={neighborlyLogoUrl}
                      alt={managedByLogo.alt || 'Neighborly Communities'}
                      style={{ ...attributionLogoStyle, ...brandMarkFilter }}
                    />
                  </a>
                </EditableSlotGroup>
              </div>
            </div>

            {/* A Cobalt Built Community — wordmark already includes its label */}
            <div className="flex h-9 w-[240px] items-center justify-center">
              <EditableSlotGroup groupPath="footer.cobalt_built_logo" definition={cobaltBuiltLogoDef}>
                <a href={cobaltBuiltLogo.url || 'https://cobaltbuilt.com/'} target={cobaltBuiltLogo._target || '_blank'} rel="noopener noreferrer" className="block hover:opacity-80 transition-opacity duration-200">
                  <img
                    src={cobaltBuiltLogoUrl}
                    alt={cobaltBuiltLogo.alt || 'A Cobalt Built Community'}
                    style={{ ...attributionLogoStyle, ...brandMarkFilter }}
                  />
                </a>
              </EditableSlotGroup>
            </div>

            {/* Powered By Multihub */}
            <div className="flex flex-col items-center">
              <span className="font-body text-[11px] uppercase tracking-[0.15em] text-primary-foreground/60 mb-3">Powered by</span>
              <div className="flex h-9 w-[240px] items-center justify-center">
                <a href="https://multihub.io" target="_blank" rel="noopener noreferrer" className="block hover:opacity-80 transition-opacity duration-200">
                  <VibesitesImage
                    url={multihubLogoUrl}
                    alt="Multihub"
                    sizes="160px"
                    style={{ ...attributionLogoStyle, height: '22px', maxWidth: '160px' }}
                  />
                </a>
              </div>
            </div>

            {/* Website Design By Repli */}
            <div className="flex flex-col items-center">
              <span className="font-body text-[11px] uppercase tracking-[0.15em] text-primary-foreground/60 mb-3">Website Design by</span>
              <div className="flex h-9 w-[240px] items-center justify-center">
                <a href="https://www.repli360.com/" target="_blank" rel="noopener noreferrer" className="block hover:opacity-80 transition-opacity duration-200">
                  <VibesitesImage url={repliWhiteLogoUrl} alt="Repli" sizes="240px" style={{ ...attributionLogoStyle, ...repliFilterStyle }} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ---- Launcher z-index fallback ----
          Cheap first pass for the real RentCafe Connect launcher. The
          `useEffect` above re-applies the same selectors with inline
          `!important` after the vendor writes its own z-index. */}
      <style>{`
        .rcc-app-cta-wrap,
        .rcc-app-controls,
        #conversations-webchat-toggle {
          z-index: 120 !important;
        }
      `}</style>

    </footer>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Footer',
  displayName: 'Footer',
  tokens: {
    'footer.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'custom_ppc_tracking_phone_number',
      suggestedFields: ['custom_ppc_tracking_phone_number', 'property_call_tracking_phone', 'property_phone'],
    },
    'footer.managed_by_text': {
      label: 'Managed By Text',
      type: 'text',
      defaultValue: 'Professionally Managed by',
    },
    'footer.copyright': {
      label: 'Copyright',
      type: 'text',
      defaultValue: '© 2026 All Rights Reserved.',
    },
    'footer.accessibility_link': {
      label: 'Accessibility Page URL',
      type: 'url',
      defaultValue: '/accessibility',
    },
    'footer.privacy_link': {
      label: 'Privacy Policy Page URL',
      type: 'url',
      defaultValue: '/privacy-policy',
    },
  },
  slotGroups: {
    'footer.logo': logoSlotDef,
    'footer.managed_by_logo': managedByLogoDef,
    'footer.cobalt_built_logo': cobaltBuiltLogoDef,
    'footer.cta_link': ctaLinkSlotDef,
    'footer.address': addressSlotDef,
  },
  collectionBindings: {
    'footer.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Footer as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Footer;
